<div
  role="dialog"
  aria-modal="true"
  class="modal-wrapper"
  data-cy="product_check_dialog"
  [attr.aria-label]="config.ariaLabel"
  [style.backgroundColor]="config.backgroundColor"
>
  <div class="modal-header">
    <h6 class="title" data-cy="dialog_title">
      {{ data.title }}
    </h6>
  </div>
  <div class="modal-body">
    @if (hasMultipleTabs) {
      <ul class="nav nav-tabs" role="tablist">
        @for (tab of tabs; track tab.type; let i = $index) {
          <li
            role="tab"
            class="nav-item"
            [id]="'tab_' + i"
            (click)="onTabChange(i)"
            [class.active]="activeTab() === i"
            [attr.aria-controls]="'tabpanel_' + i"
            [attr.aria-selected]="activeTab() === i"
            [attr.tabindex]="activeTab() === i ? 0 : -1"
            [style.fontFamily]="config.tabConfig?.fontFamily"
            (keydown.enter)="onTabChangeKey($event, i)"
            (keydown.space)="onTabChangeKey($event, i)"
            (keydown.end)="onTabArrowKey($event, i)"
            (keydown.home)="onTabArrowKey($event, i)"
            (keydown.arrowLeft)="onTabArrowKey($event, i)"
            (keydown.arrowRight)="onTabArrowKey($event, i)"
            [attr.data-cy]="tabDataCy(tab.type)"
            [style.color]="
              activeTab() === i
                ? config.tabConfig?.activeColor
                : config.tabConfig?.inactiveColor
            "
            [style.background]="
              activeTab() === i
                ? config.tabConfig?.activeBackground
                : config.tabConfig?.inactiveBackground
            "
            [style.borderBottom]="
              activeTab() === i
                ? config.tabConfig?.activeBorderBottom
                : config.tabConfig?.inactiveBorderBottom
            "
          >
            {{ tab.label }}
          </li>
        }
      </ul>
    }
    @if (scanTab; as scan) {
      <div
        class="pane"
        [id]="'tabpanel_' + scanIndex"
        data-cy="product_check_scan_pane"
        [hidden]="activeTab() !== scanIndex"
        [attr.role]="hasMultipleTabs ? 'tabpanel' : null"
        [attr.aria-labelledby]="hasMultipleTabs ? 'tab_' + scanIndex : null"
      >
        <div
          class="dialog-titles"
          [style.color]="scan.color"
          [style.backgroundColor]="scan.backgroundColor"
        >
          <h2 class="title dialog-main-title">
            {{ scan.title }}
          </h2>
          @if (!!scan.subtitle) {
            <div class="title dialog-subtitle">
              {{ scan.subtitle }}
            </div>
          }
        </div>

        @if (isScanComplete()) {
          @let result = product();
          <div
            class="scan-result"
            aria-live="polite"
            [attr.aria-busy]="isChecking()"
          >
            <div class="result-container" [class.checking]="isChecking()">
              @if (isChecking()) {
                <div class="loader" aria-hidden="true"></div>
                @if (scan.checkingMessage) {
                  <div class="result-label">{{ scan.checkingMessage }}</div>
                }
                <div class="code">{{ detectedBarcode() }}</div>
              } @else if (result) {
                <div class="result-label found">
                  {{ scan.successMessage }}
                </div>
                <div class="code" data-cy="result_label">
                  {{ result.label }}
                </div>
                <div class="code" data-cy="result_code">
                  {{ result.barcodeNumber }}
                </div>
              } @else if (checkFailed()) {
                <div class="result-label not-found">
                  {{ scan.failureMessage || scan.errorMessage }}
                </div>
                <div class="code">{{ detectedBarcode() }}</div>
              } @else {
                <div class="result-label not-found">
                  {{ scan.errorMessage }}
                </div>
                <div class="code">{{ detectedBarcode() }}</div>
              }
            </div>
          </div>
        }

        <div id="barcode-scanner">
          @defer (when activeTab() === scanIndex; prefetch on idle) {
            <!-- Nested @if, not the @defer condition alone: a deferred block
                 never reverts once triggered, and the pane is only [hidden], so
                 without this the camera track stays live for the rest of the
                 dialog once the shopper switches to another tab. -->
            @if (activeTab() === scanIndex) {
              <b2c-scanner
                [readers]="scan.readers"
                (scanComplete)="onScanComplete()"
                [permissionDeniedMessage]="scan.errorWhenCameraPermissionDenied"
                [retryPermissionButtonLabel]="scan.retryPermissionButtonLabel"
                [cameraUnavailableMessage]="scan.errorWhenCameraNotFound"
                [cameraBusyMessage]="scan.errorWhenCameraBusy"
                [startButtonLabel]="scan.startButtonLabel"
                [buttonLabel]="scan.restartButtonLabel"
                [previewLabel]="scan.previewLabel"
                (scanSuccess)="onScanSuccess($event)"
              />
            }
          }
        </div>
      </div>
    }
    @if (manualTab; as manual) {
      <div
        class="pane"
        data-cy="product_check_manual_pane"
        [id]="'tabpanel_' + manualIndex"
        [hidden]="activeTab() !== manualIndex"
        [attr.role]="hasMultipleTabs ? 'tabpanel' : null"
        [attr.aria-labelledby]="hasMultipleTabs ? 'tab_' + manualIndex : null"
      >
        <!-- Not deferred: this pane is typically the default view (index 0),
        so a lazy chunk would open the modal on an empty body. -->
        <b2c-manual-barcode-check
          [tab]="manual"
          [buttonStyles]="buttonStyles"
        />
      </div>
    }
    @if (productListTab; as list) {
      <div
        class="pane"
        data-cy="product_check_barcode_pane"
        [id]="'tabpanel_' + productListIndex"
        [hidden]="activeTab() !== productListIndex"
        [attr.role]="hasMultipleTabs ? 'tabpanel' : null"
        [attr.aria-labelledby]="
          hasMultipleTabs ? 'tab_' + productListIndex : null
        "
      >
        @defer (when activeTab() === productListIndex; prefetch on idle) {
          <b2c-manual-product-check [tab]="list" />
        }
      </div>
    }
  </div>

  <div class="modal-footer">
    <b2c-button
      type="button"
      class="close-btn"
      data-cy="close_btn"
      (click)="closeDialog()"
      [styles]="buttonStyles"
      [attr.aria-label]="closeButtonConfig.ariaLabel"
    >
      {{ closeButtonConfig.label }}</b2c-button
    >
  </div>
</div>
